import React from "react";
import { Box, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { Delete } from "@mui/icons-material";
import PortfolioUpload from "../PortfolioUpload";
import ProjectCard from "../Portfolio/ProjectCard";
import EmptyPortfolioPreviewState from "./EmptyPortfolioPreviewState";
import { usePortfolioData } from "../Portfolio/usePortfolioData";
import { useCRUDPermission } from "../hooks/usePermission";

const PortfolioPreview = () => {
  // Check PROFILE CREATE permission to show/hide upload CTA
  const { hasPermission: hasProfileCreate } = useCRUDPermission("PROFILE", "CREATE");
  const {
    portfolios,
    loading,
    uploadModalOpen,
    editingProject,
    deleteDialogOpen,
    projectToDelete,
    deleting,
    getOrderedImages,
    handleUploadSuccess,
    handleCreateAlbum,
    handleEditProject,
    handleDeleteProject,
    handleCloseModal,
    handleCloseDeleteDialog,
    confirmDelete
  } = usePortfolioData('preview');

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If we have portfolio data, show the preview (limited to 6 items)
  if (portfolios.length > 0) {
    return (
      <Box sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, margin: 0 }}>
            Portfolio
          </Typography>
        </Box>
        
        {/* Portfolio Grid - Limited to 6 items for preview */}
        <Box
          sx={{
            display: 'grid',
            // min() keeps the track from exceeding the container on a phone,
            // where a rigid 320px column overflowed.
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 320px))',
            gap: 2,
            justifyContent: 'flex-start',
          }}
        >
          {portfolios.slice(0, 6).map((project) => {
            const orderedImages = getOrderedImages(project);
            return (
              <ProjectCard
                key={project.id}
                project={project}
                orderedImages={orderedImages}
                onEdit={handleEditProject}
                onDelete={handleDeleteProject}
                showFullDescription={false}
              />
            );
          })}
        </Box>

        {/* Show "View All" button if there are more than 6 projects */}
        {portfolios.length > 6 && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Showing 6 of {portfolios.length} projects
            </Typography>
          </Box>
        )}


        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Project</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{projectToDelete?.project_name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDeleteDialog}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
        
        <PortfolioUpload
          key={editingProject?.id || 'new'}
          open={uploadModalOpen}
          onClose={handleCloseModal}
          onSuccess={handleUploadSuccess}
          editingProject={editingProject}
        />
      </Box>
    );
  }

  // Show empty state
  return (
    <>
      <EmptyPortfolioPreviewState 
        onCreateAlbum={hasProfileCreate ? handleCreateAlbum : undefined} 
        readOnly={!hasProfileCreate}
      />
      <PortfolioUpload
        key={editingProject?.id || 'new'}
        open={uploadModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleUploadSuccess}
        editingProject={editingProject}
      />
    </>
  );
};

export default PortfolioPreview;